import { BanknoteIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';

interface MonthlySalaryBulkToggleProps {
  /** How many loaded members are eligible for monthly-salary billing. */
  eligibleCount: number;
  /** True when every eligible member currently has monthly salary enabled. */
  isAllEnabled: boolean;
  onToggle: (isEnabled: boolean) => void;
}

/**
 * Table-wide control that switches every eligible member to (or away from) monthly-salary
 * billing in one action.
 *
 * Rendered rather than hidden when no member is eligible, with the reason in a tooltip —
 * silently omitting it would leave the user unable to tell whether the feature is missing
 * or simply not applicable to this billing period.
 */
export const MonthlySalaryBulkToggle = ({
  eligibleCount,
  isAllEnabled,
  onToggle,
}: MonthlySalaryBulkToggleProps) => {
  const { t } = useTranslation('authenticated.bills', { keyPrefix: 'monthlySalary' });

  const hasEligibleMembers = eligibleCount > 0;

  const control = (
    <div className="flex items-center gap-2">
      <BanknoteIcon
        size={16}
        className={cn('shrink-0', hasEligibleMembers ? 'text-muted-foreground' : 'text-muted')}
      />
      <span className={cn('text-sm', !hasEligibleMembers && 'text-muted-foreground')}>
        {t('bulkToggleLabel', { count: eligibleCount })}
      </span>
      <Switch
        checked={isAllEnabled}
        disabled={!hasEligibleMembers}
        onCheckedChange={onToggle}
        aria-label={t('bulkToggleAriaLabel')}
      />
    </div>
  );

  if (hasEligibleMembers) {
    return control;
  }

  return (
    <SimpleTooltip content={t('bulkToggleNoneEligibleTooltip')}>
      <span className="inline-flex cursor-help">{control}</span>
    </SimpleTooltip>
  );
};
